/*
 * Pannellum - An HTML5 based Panorama Viewer
 * Copyright (c) 2011-2017 Matthew Petroff
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */
import EventEmmiter from './helpers/EventEmmiter';
import Renderer from './libpannellum';
import E3HotspotTooltip from './e3-hotspot-tooltip/e3-hotspot-tooltip';
import EffectZoom from './zoom-effect/index';
import AnimatedHotspots from './animated-hotspot/animated-hotspots';
import BlockedBox from './service/blockedBox/blockedBox';
import {updateInfoIconStyle} from 'lib/helpers/common';
import {arrI, getIcon} from 'a_root-folder/infoicons/index';
import HelpService from "./service/help.service";
import {size4K} from "../constants";
import {MAXIMUM_PIXEL_IOS} from 'a_root-folder/constants';
import {limitSize} from 'service/html.service';
import {MEASUREMENT} from 'a_common-folder/constants';
import Timer from './helpers/timeout';
import transform from './helpers/transform';
import controlsKeycodes from './helpers/controls-keynumbers';
import minAspect from './helpers/aspect-ratio-breakpoint';

const cloneDeep = require('lodash/cloneDeep');
const merge = require('lodash/merge');
const throttle = require('lodash/throttle');
const debounce = require('lodash/debounce');
const detectBrowser = require('a_common-folder/utils/detectBrowser');

class Quaternion {
    constructor(w, x, y, z) {
        this.w = w;
        this.x = x;
        this.y = y;
        this.z = z;
    }

    multiply(q) {
        return new Quaternion(this.w * q.w - this.x * q.x - this.y * q.y - this.z * q.z,
            this.x * q.w + this.w * q.x + this.y * q.z - this.z * q.y,
            this.y * q.w + this.w * q.y + this.z * q.x - this.x * q.z,
            this.z * q.w + this.w * q.z + this.x * q.y - this.y * q.x);
    }

    toEulerAngles() {
        let phi = Math.atan2(2 * (this.w * this.x + this.y * this.z),
            1 - 2 * (this.x * this.x + this.y * this.y)),
            theta = Math.asin(2 * (this.w * this.y - this.z * this.x)),
            psi = Math.atan2(2 * (this.w * this.z + this.x * this.y),
                1 - 2 * (this.y * this.y + this.z * this.z));
        return [phi, theta, psi];
    }
}

export default class Pannellum extends EventEmmiter {
    static RADIAN = Math.PI / 180;
    static performance = typeof performance !== 'undefined' ? performance : Date;
    static _usedKeyNumbers = [16, 17, 27, 37, 38, 39, 40, 61, 65, 68, 83, 87, 107, 109, 173, 187, 189];
    static _defaultConfig = {
        hfov: 120,
        minHfov: 30,
        maxHfov: 140,
        pitch: 0,
        minPitch: -90,
        maxPitch: 90,
        yaw: 0,
        minYaw: -180,
        maxYaw: 180,
        roll: 0,
        haov: 360,
        vaov: 180,
        vOffset: 0,
        autoRotate: false,
        showFullscreenCtrl: true,
        doubleClickZoom: true,
        mouseZoom: true,
        animationTimingFunction: Pannellum.timingFunctionEaseInOutQuad,
        posePitchDegrees: 0,
        poseRollDegrees: 0
    };
    _timer = new Timer();
    _firstRender = false;
    _current = {};
    _mresConfig = null;
    _origHfov = 120;
    _origPitch = 0;
    _listenersInited = false;
    _movesBlocked = false;
    _stateListening = false;
    _isUserInteracting = false;
    _loaded = false;
    _keysDown = new Array(16).fill(false);
    _animatedMove = {};
    _speed = {yaw: 0, pitch: 0, hfov: 0};
    _prevAnimationTime = null;
    _latestInteractionTime = null;
    _animationRequested = false;
    _interactionData = {pointerIds: [], pointerCoords: []};
    _fullscreenActive = false;
    _lastNormalizedHfov = [];
    _prevSentData = {};
    _initialConfig = null;
    _blockTimeout = null;
    _blockMoveScene = null;
    _currentSceneId = null;
    _listenKeyboardEvents = true;
    _orientation = false;
    _orientationYawOffset = 0;
    _hostName = window.location.protocol + '//' + window.location.hostname;
    _disableDevicePixelRatio = false;
    _containerParams = {width: null, height: null};
    _animatedHotspots = null;
    _resetHotspotConfig = throttle((hs) => {
        hs.clicked = false;
    }, 700);
    action = 'view';
    activeMode = null;

    _onKeyDown = this._onKeyDown.bind(this);
    _onKeyUp = this._onKeyUp.bind(this);
    _onDocumentResizeBound = this._onDocumentResize.bind(this);
    _onFullScreenChangeBound = this._onFullScreenChange.bind(this);
    _orientationListenerBound = this._orientationListener.bind(this);
    _onDocumentDoubleClickBound = this._onDocumentDoubleClick.bind(this);
    _onDocumentMouseDownBound = this._onDocumentMouseDown.bind(this);
    _onDocumentTouchStartBound = this._onDocumentTouchStart.bind(this);
    _onDocumentMouseMoveBound = this._onDocumentMouseMove.bind(this);
    _onDocumentTouchMoveBound = this._onDocumentTouchMove.bind(this);
    _onDocumentMouseUpBound = this._onDocumentMouseUp.bind(this);
    _onDocumentTouchEndBound = this._onDocumentTouchEnd.bind(this);
    _onDocumentPointerDownBound = this._onDocumentPointerDown.bind(this);
    _onDocumentPointerMoveBound = this._onDocumentPointerMove.bind(this);
    _onDocumentPointerUpBound = this._onDocumentPointerUp.bind(this);
    _onDocumentMouseWheelBound = this._onDocumentMouseWheel.bind(this);
    _onDocumentKeyPressBound = this._onDocumentKeyPress.bind(this);
    _clearKeysBound = this._clearKeys.bind(this);
    _onDocumentKeyUpBound = this._onDocumentKeyUp.bind(this);
    _createHotSpotBound = this._createHotSpot.bind(this);
    _animateBound = this._animate.bind(this);
    _isAcessibility = false;


    _movingThrottle = throttle((code, data) => {
        if (Math.abs(this._current.yaw - this._current.last_yaw) < 3 && Math.abs(this._current.pitch - this._current.last_pitch) < 3 && this.moving) {
            this.sendSyncAction(code, data);
            this.moving = false
        } else {
            this._blockScreenThrottle();
            this.moving = true
        }

        this._current.last_yaw = this._current.yaw;
        this._current.last_pitch = this._current.pitch;
    }, 300);


    _blockScreenThrottle = throttle(() => {
        // console.info('send action for block screen')
        this._sendBlockScreen();
    }, 2000);

    _sendBlockScreen() {
        if (this._movesBlocked) return;
        this._fireEvent('e3-viewer-block-screen', {});
    }

    setMuseumMode(enabled) {
        this._museumMode = !!enabled;
    }

    // Museum guide flow: scene hotspots can't be used, so don't show them.
    setSceneHotspotsHidden(hidden) {
        this._sceneHotspotsHidden = !!hidden;
        if (this._container) this._container.classList.toggle('hidden-scene-hotspots', this._sceneHotspotsHidden);
    }

    setHorizontalTransitions(enabled) {
        this.horizontalTransitions = !!enabled;
    }

    sendSyncAction(code, data) {
        let obj = this._convertPannellumJSON(data);
        // console.log(`%c send action, ${code}, ${data})`, 'background: #222; color: #bada55');
        if (this._movesBlocked) return;
        this._fireEvent(code, obj);
        this._prevSentData = obj;

        return obj;
    };

    changeTiming(mode=this.mode) {
        if(mode === 'e3autovideo'){
            this._current = Object.assign(this._current, cloneDeep({animationTimingFunction: Pannellum.timingFunctionLinear}));
        }else {
            this._current = Object.assign(this._current, cloneDeep({animationTimingFunction: Pannellum.timingFunctionLinear}));
        }
    };

    setMode(mode) {
        if(this.mode !== mode) this.changeTiming(mode);
        this.mode = mode;
    };

    setActiveMode(mode) {
        this.activeMode = mode;
    };

    e3PlayEvent(action, obj={}) {
        this._fireEvent(`e3-play-${action}`, obj);
    };

    infoModalEvent(action, obj={}) {
        this._fireEvent(`info-modal-${action}`, obj)
    };

    _sendSyncActionThrottle() {
        if (!this._movesBlocked && this._canSendAction) {
            this._movingThrottle("e3-viewer-send-coord", cloneDeep(this._current));
        }
    }

    _movingDebounce = debounce(() => {
        // console.log('send from debounce')
        if (!this._movesBlocked && this._canSendAction) {
            this.sendSyncAction("e3-viewer-send-coord", cloneDeep(this._current));
        }
    }, 1000);

    constructor(container, initialConfig, translateService) {
        super();
        this._translateService = translateService;
        this._translate = translateService.translate.bind(translateService);
        this._prepareInitialConfig(initialConfig);
        this._animatedHotspots = new AnimatedHotspots();
        this._e3liveRoomId = window.location.search.split('room=');
        this._converte3video = window.location.search.split('converte3video=');
        //remove editable prop, use tour-admin.js to work with viewer in admin panel
        this._editable = !!initialConfig.editable || false;
        this._orientationSupportPromise = this._getOrientationSupport();
        this._helperService = new HelpService();
        this._urlParams = this._helperService.urlParams;
        this._fadetransition = this._urlParams.fadetransition === 'false' ? false : true;
        this._permissions = this._helperService.getPermissions();
        this.horizontalTransitions = this._permissions['horizontal_transitions']  === 'false' ? false : true;
        container = typeof container === 'string' ? document.getElementById(container) : container;
        container.classList.add('pnlm-container');
        container.tabIndex = 0;
        this._container = container;

        let renderContainer = document.createElement('div');
        renderContainer.className = 'pnlm-render-container';
        container.appendChild(renderContainer);
        this._renderContainer = renderContainer;

        let dragFix = document.createElement('div');
        dragFix.className = 'pnlm-dragfix';
        container.appendChild(dragFix);
        this._addBlockedBox();

        // this.hideBlockScreen();
        this._dragFix = dragFix;
        this._dragFix.addEventListener('contextmenu', this._aboutMessage);

        this._mergeConfig(initialConfig.default && initialConfig.default.firstScene);

        this._disableDevicePixelRatio = !this._editable && this._current.autoRotate;

        this._updateContainerProps();

        this._hotspotTooltip = new E3HotspotTooltip({
            className: 'e3-hotspot-tooltip',
            titleTag: 'canvas',
            loadingClassName: 'e3-hotspot-tooltip--loading'
        });

        this._init();

        // var createHS = createHotSpot;
        // var createHotSpot = function (hs) {
        //     createHS(hs);
        //     if (hs.cssClass && hs.div.classList.contains('hotspot-zoomable')) {
        //         hs.div.dataset.width = 200;
        //         hs.div.dataset.height = 200;
        //     }
        // };
        // var renderHS = renderHotSpot;
        // var renderHotSpot = function (hs, pitch, yaw) {
        //     renderHS(hs, pitch, yaw);
        //     if (hs.div.style.visibility == 'visible') {
        //         var transform = hs.div.style.transform;
        //         if (hs.div.classList.contains('hotspot-zoomable')) {
        //             transform = transform.replace(/rotate\(\d+deg\)/, '');
        //             transform += ' rotate3d(1, 0, 0, ' + (-config.pitch + 90) + 'deg)';
        //             transform += ' rotate3d(0, 0, 1, ' + (config.yaw) + 'deg)';
        //             hs.div.style.webkitTransform = transform;
        //             hs.div.style.MozTransform = transform;
        //             hs.div.style.transform = transform;
        //         }
        //
        //     }
        // };

    }

    static applyCameraCorrection(yaw, pitch, roll, posePitchDegrees, poseRollDegrees, rad = false) {
        let r = roll,
            yw = yaw,
            pt = pitch,
            pp = posePitchDegrees,
            pr = poseRollDegrees;

        if (!rad) {
            r *= Pannellum.RADIAN;
            yw *= Pannellum.RADIAN;
            pt *= Pannellum.RADIAN;
            pp *= Pannellum.RADIAN;
            pr *= Pannellum.RADIAN;
        }


        // Apply matrix rotation: R(x)^-1 * R(y)^-1

        let _pitch = pt, // origin value
            _yaw = yw, // origin value
            x = Math.cos(pr) * Math.sin(pt) * Math.sin(pp) +
                Math.cos(pt) * (Math.cos(pp) * Math.cos(yw) +
                    Math.sin(pr) * Math.sin(pp) * Math.sin(yw)),
            y = -Math.sin(pt) * Math.sin(pr) +
                Math.cos(pt) * Math.cos(pr) * Math.sin(yw),
            z = Math.cos(pr) * Math.cos(pp) * Math.sin(pt) +
                Math.cos(pt) * (-Math.cos(yw) * Math.sin(pp) +
                    Math.cos(pp) * Math.sin(pr) * Math.sin(yw));
        pt = Math.asin(Math.max(Math.min(z, 1), -1));
        yw = Math.atan2(y, x);

        // Calculate roll
        let v = [Math.cos(_pitch) * (Math.sin(pr) * Math.sin(pp) * Math.cos(_yaw) - Math.cos(pp) * Math.sin(_yaw)),
                Math.cos(_pitch) * Math.cos(pr) * Math.cos(_yaw),
                Math.cos(_pitch) * (Math.cos(pp) * Math.sin(pr) * Math.cos(_yaw) + Math.sin(_yaw) * Math.sin(pp))],
            w = [-Math.cos(pt) * Math.sin(yw), Math.cos(pt) * Math.cos(yw)];
        // accept also as dyaw
        let roll_adj = Math.acos(Math.max(Math.min((v[0] * w[0] + v[1] * w[1]) /
            (Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]) *
                Math.sqrt(w[0] * w[0] + w[1] * w[1])), 1), -1));

        // calculate pitch adjustment
        let p = [-Math.cos(pt) * Math.sin(yw)];

        let pitch_adj = Math.acos(Math.max(Math.min((v[1] * p[0]) / (Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]) * Math.sqrt(p[0] * p[0])), 1), -1));
        if (v[2] < 0) {
            roll_adj = 2 * Math.PI - roll_adj;
        }

        return [yw, pt, r + roll_adj, pitch_adj];
    }

    static rotateEquiPoint(yaw, pitch, posePitch, poseRoll) {
        let yw = yaw,
            pt = pitch,
            pp = posePitch,
            pr = poseRoll,

            x = Math.cos(pt) * (Math.cos(yw) * Math.cos(pp) + Math.sin(yw) * Math.sin(pr) * Math.sin(pp)) - Math.sin(pp) * Math.sin(pt) * Math.cos(pr),
            y = Math.cos(pt) * Math.sin(yw) * Math.cos(pr) + Math.sin(pt) * Math.sin(pr),
            z = Math.cos(pt) * (Math.cos(yw) * Math.sin(pp) - Math.sin(yw) * Math.sin(pr) * Math.cos(pp)) + Math.cos(pp) * Math.sin(pt) * Math.cos(pr);

        return [Math.atan2(y, x), Math.asin(Math.max(Math.min(z, 1), -1))];
    }

    static revertCameraPosition(yaw, pitch, posePitch, poseRoll) {
        let yw = yaw,
            pt = pitch,
            pp = posePitch,
            pr = poseRoll,

            x = Math.cos(pt) * Math.cos(yw) * Math.cos(pp) + Math.sin(pt) * Math.sin(pp),
            y = Math.cos(pt) * (Math.sin(yw) * Math.cos(pr) + Math.sin(pr) * Math.cos(yw) * Math.sin(pp)) - Math.sin(pr) * Math.sin(pt) * Math.cos(pp),
            z = Math.cos(pt) * (Math.sin(yw) * Math.sin(pr) - Math.cos(pr) * Math.cos(yw) * Math.sin(pp)) + Math.cos(pr) * Math.sin(pt) * Math.cos(pp);
        pt = Math.asin(Math.max(Math.min(z, 1), -1));
        yw = Math.atan2(y, x);

        return [yw, pt];
    }

    static timingFunctionEaseInOutQuad(t) {
        // easeInOutQuad from https://gist.github.com/gre/1650294
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }
    static timingFunctionLinear(t) {
        // easeInOutQuad from https://gist.github.com/gre/1650294
        return t;
    }

    static _taitBryanToQuaternion(alpha, beta, gamma) {
        let r = [beta ? beta * Pannellum.RADIAN / 2 : 0,
            gamma ? gamma * Pannellum.RADIAN / 2 : 0,
            alpha ? alpha * Pannellum.RADIAN / 2 : 0];
        let c = [Math.cos(r[0]), Math.cos(r[1]), Math.cos(r[2])],
            s = [Math.sin(r[0]), Math.sin(r[1]), Math.sin(r[2])];

        return new Quaternion(c[0] * c[1] * c[2] - s[0] * s[1] * s[2],
            s[0] * c[1] * c[2] - c[0] * s[1] * s[2],
            c[0] * s[1] * c[2] + s[0] * c[1] * s[2],
            c[0] * c[1] * s[2] + s[0] * s[1] * c[2]);
    }

    static _computeQuaternion(alpha, beta, gamma) {
        // Convert Tait-Bryan angles to quaternion
        let quaternion = Pannellum._taitBryanToQuaternion(alpha, beta, gamma);
        // Apply world transform
        quaternion = quaternion.multiply(new Quaternion(Math.sqrt(0.5), -Math.sqrt(0.5), 0, 0));
        // Apply screen transform
        let angle = window.orientation ? -window.orientation * Pannellum.RADIAN / 2 : 0;
        return quaternion.multiply(new Quaternion(Math.cos(angle), 0, -Math.sin(angle), 0));
    }

    _aboutMessage(event) {
        event.preventDefault();
        return false;
    }


    _addBlockedBox() {
        this.blockedBox = new BlockedBox(document.body);
        this.blockedBox.hide();
    }

    _addHotSpot(hs, sceneId) {
        if (!sceneId && sceneId !== 0) {
            // Add to current scene
            this._createHotSpot(hs);
            this._current.hotSpots.push(hs);
            this._renderHotSpot(hs);
        } else {
            // Add to a different scene
            if (this._initialConfig.scenes.hasOwnProperty(sceneId))
                this._initialConfig.scenes[sceneId].hotSpots.push(hs);
            else
                throw 'Invalid scene ID!'
        }
        return this;
    }

    _addScene(sceneId, config) {
        this._initialConfig.scenes[sceneId] = cloneDeep(config);
        return this;
    }

    //++
    _anError(errorMsg) {
        console.error(errorMsg || 'Your browser does not have the necessary WebGL support to display this panorama.')
        this._fireEvent('error', errorMsg);
    }

    _animate() {
        this._render();
        if (this._isUserInteracting || this._orientation) {
            requestAnimationFrame(this._animateBound);
        } else if (this._isActionKeyDown()
            || this._current.autoRotate
            || this._current.autoRotate && this._e3liveRoomId.length === 1
            || this._animatedMove.pitch || this._animatedMove.yaw || this._animatedMove.hfov
            || Math.abs(this._speed.yaw) > 0.01 || Math.abs(this._speed.pitch) > 0.01 || Math.abs(this._speed.hfov) > 0.01) {
            this._keyRepeat();
            if (this._isActionKeyDown()) {
                this._continueMovingSync();
            }
            requestAnimationFrame(this._animateBound);
        } else if (this._renderer && this._renderer.isLoading()) {
            requestAnimationFrame(this._animateBound);
        } else {
            this._animationRequested = false;
            this._prevAnimationTime = null;
        }
    }

    getCurrentPosition() {
        return this._convertPannellumJSON(this._current);
    }

    /**
     * Check if images are loading.
     * @memberof Renderer
     * @instance
     * @returns {boolean} Whether or not images are loading.
     */
    isLoading() {
        return this._renderer.isLoading();
    }

    _zoomEffectFromCenter(callback) {
        const { width, height } = this._containerParams;
        const canvas = this._container.querySelector('canvas');
        if (!canvas) return callback();
        const effectZoom = new EffectZoom(this._container, { width, height, x: width / 2, y: height / 2 });
        effectZoom.addCanvas(canvas.toDataURL(), () => {
            effectZoom.runAnimation(() => {});
            callback();
        });
    }

    _zoomEffectTransaction(callback, hs) {
        const {width, height} = this._containerParams;
        const hotspot = this._current.hotSpots.find((hotspot) => hotspot.sceneId === hs.sceneId);
        if(!hotspot) {
            return callback();
        }
        const  {x, y, width: divWidth} = hotspot.div.getBoundingClientRect();
        const effectZoom = new EffectZoom(this._container, {width, height, x: x + divWidth, y})
        const dataUrl = this._container.querySelector('canvas').toDataURL();

        effectZoom.addCanvas(dataUrl, () => {
            effectZoom.runAnimation((opacityTime) => {

            });
            callback();
        });
    }

    _fadeAnimateHotspotTransition(hs, callback){
        this._zoomEffectTransaction(() => {
            this._onStateChange('end', 'coord');
            this._fireEvent('hotspotclick', hs.lineID);
            hs.clicked = false;
            this._loadScene(hs.sceneId, hs.targetPitch, hs.targetYaw, hs.targetHfov, () => {
                if (this._orientation) {
                    this._startOrientation(true);
                }
            });

            callback();
        }, hs);
    }

    _animateHotspotTransition(hs, callback = () => {
    }, hfov = 10) {
        if(this._fadetransition) return this._fadeAnimateHotspotTransition(hs, callback);

        this.lookAt(hs.pitch, hs.yaw, hfov, 700, () => {
            setTimeout(() => {
                this._onStateChange('end', 'coord');
                this._fireEvent('hotspotclick', hs.lineID);
                hs.clicked = false;
                this._loadScene(hs.sceneId, hs.targetPitch, hs.targetYaw, hs.targetHfov, false, () => {
                    if (this._orientation) {
                        this._startOrientation(true);
                    }
                });
                callback();
            }, 50);
        });
    }

    _animateInit() {
        if (this._animationRequested) {
            return;
        }
        this._animationRequested = true;
        this._animate();
    }

    _animateMove(axis) {
        let val = this._animatedMove[axis];
        let normalizedTime = Math.min(1, Math.max((Date.now() - val.startTime) / val.duration, 0));
        let time = this._current.animationTimingFunction(normalizedTime);
        let result = val.startPosition + time * (val.endPosition - val.startPosition);
        // console.log('_animateMove - ', normalizedTime,  time, result)
        // if ((val.endPosition > val.startPosition && result >= val.endPosition) ||
        //     (val.endPosition < val.startPosition && result <= val.endPosition) ||
        //     val.endPosition === val.startPosition) {
        if (time >= 1) {
            result = val.endPosition;
            this._speed[axis] = 0;
            let callback = this._animatedMove[axis].callback,
                callbackArgs = this._animatedMove[axis].callbackArgs;
            delete this._animatedMove[axis];
            if (typeof callback === 'function')
                callback(callbackArgs);
        }

        this._current[axis] = result;
    }

    _clearKeys() {
        this._keysDown = this._keysDown.map(e => false);
    }

    _constrainHfov(hfov) {
        if (hfov < this._current.minHfov) {
            return this._current.minHfov;
        } else if (hfov > this._current.maxHfov) {
            return this._current.maxHfov;
        } else {
            return hfov;
        }
    }

    _continueMovingSync() {
        this._sendSyncActionThrottle();
        this._movingDebounce();
    }

    _continueInteracting(eventObjects) {
        if (this._isUserInteracting && this._loaded) {
           this._onMouseMove(eventObjects);
        }
    }

    _onMouseMove(eventObjects) {
        this._continueMovingSync();
        this._latestInteractionTime = Date.now();

        let pos = this._mousePosition(eventObjects[0]);
        let {x, y} = pos;

        if (eventObjects.length === 2 && this._interactionData.dist !== -1) {
            let pos2 = this._mousePosition(eventObjects[1]);
            let dx = pos2.x - x;
            let dy = pos2.y - y;
            x += dx * .5;
            y += dy * .5;
            let clientDist = Math.sqrt(dx * dx + dy * dy);
            this._setHfov(this._current.hfov + (this._interactionData.dist - clientDist) * 0.1);
            this._interactionData.dist = clientDist;
        }

        let [canvasWidth, canvasHeight] = this._renderer.getCanvasDimensions();

        if (!this._disableDevicePixelRatio) {
            canvasWidth /= window.devicePixelRatio || 1;
            canvasHeight /= window.devicePixelRatio || 1;
        }

        const {yaw: startYaw, pitch: startPitch} = this._current;

        let yaw = ((Math.atan(this._interactionData.x / canvasWidth * 2 - 1) - Math.atan(x / canvasWidth * 2 - 1)) / Pannellum.RADIAN * this._current.hfov / 90) + this._interactionData.yaw;
        this._speed.yaw = (yaw - this._current.yaw) % 360 * 0.2;
        this._current.yaw = yaw;

        let vfov = 2 * Math.atan(Math.tan(this._current.hfov / 2 * Pannellum.RADIAN) * canvasHeight / canvasWidth) / Pannellum.RADIAN;

        let pitch = ((Math.atan(y / canvasHeight * 2 - 1) - Math.atan(this._interactionData.y / canvasHeight * 2 - 1)) / Pannellum.RADIAN * vfov / 90) + this._interactionData.pitch;
        this._speed.pitch = (pitch - this._current.pitch) * 0.2;
        this._current.pitch = pitch;

        this._fireEvent('viewer-orientation-change', {yaw, pitch, startYaw, startPitch});
    }

    onMouseMove(eventObjects) {
        this._onMouseMove(eventObjects);
        this._animateInit();
    }

    _convertPannellumJSON(data) {
        return {
            ...data,
            scene: data.scene || data.sceneId || null,
        }
    }

    _createHotSpot(hotspotData, ind) {
        const hotspotURL = this._initialConfig.hotspot_url;

        // Make sure hot spot pitch and yaw are numbers
        hotspotData.pitch = Number(hotspotData.pitch) || 0;
        hotspotData.yaw = Number(hotspotData.yaw) || 0;

        let div = document.createElement('div');
        div.className = `pnlm-hotspot-base pnlm-${this._escapeHTML(hotspotData.type)} ${hotspotData.cssClass || ''}`;
        div.className += ' icon-custom';
        if (arrI.includes(hotspotData.icon)) div.className += ' icon-default';
        const imgBlock = document.createElement('div');
        div.appendChild(imgBlock);

        if (hotspotData.icon) {
            const img = document.createElement('img');
            // if(!detectBrowser.isIE){
            //     img.style = `content: url(${getIcon(hotspotData.icon)})`;
            // }else{
            img.src = getIcon(hotspotData.icon)
            // }
            imgBlock.className += ' icon-img';
            imgBlock.appendChild(img);
        }

        div.className += ' icon-' + hotspotData.icon;
        div.className += ' info-' + hotspotData.id;
        hotspotData.div = div;
        hotspotData.clicked = false;

        if (hotspotData.title || hotspotData.type === 'info') {
            let span = document.createElement('span');
            span.className = 'pnlm-hotspot-title';
            hotspotData.span = div;
            span.innerHTML = hotspotData.title ? this._escapeHTML(hotspotData.title) : '';
            imgBlock.appendChild(span);
            hotspotData.span = span;

            if(hotspotData.type === 'scene'){
                let accessibilityHotspot = document.createElement('span');
                accessibilityHotspot.classList.add('accessibility-hotspot');
                accessibilityHotspot.classList.add('hidden');
                accessibilityHotspot.dataset.sceneID = hotspotData.id;
                const count = document.querySelectorAll('.accessibility-hotspot').length;
                accessibilityHotspot.dataset.id = count;
                accessibilityHotspot.textContent = count;
    
                hotspotData.accessibility = span;
                div.appendChild(accessibilityHotspot);
            }
        }

        if (hotspotData.type === 'scene' && this._animatedHotspots.isAnimated(hotspotURL)) {
            let animationTemplate = this._animatedHotspots.getTemplate(hotspotURL);

            if (animationTemplate) {
                div.appendChild(animationTemplate);
            }
        }

        this._initHotspotEvents(hotspotData);

        this._renderContainer.appendChild(div);
    }

    _createHotSpots() {
        if (!this._current.hotSpots) {
            this._current.hotSpots = [];
            return;
        }

        // Sort by pitch so tooltip is never obscured by another hot spot
        this._current.hotSpots
            .sort((a, b) => a.pitch - b.pitch)
            .forEach(this._createHotSpotBound);

        // if (this._stateListening) {
        //TOOD: move to pannellum-admin
        // disableAllHotspotsDrag();
        // this._toggleHotSpotVisibility('info', 'none');
        // }

        this._renderHotSpots();
        updateInfoIconStyle();
    }

    _createSceneElements() {
        this._createHotSpots();
    }

    _destroyHotSpots() {
        if (this._current.hotSpots) {
            this._current.hotSpots.forEach(hs => {
                let el = hs.div;

                // todo: check if tooltip is present for hs
                this._destroyHotSpotEvents(hs);

                while (el.parentNode !== this._renderContainer) {
                    el = el.parentNode;
                }
                this._renderContainer.removeChild(el);
                el = null;
                delete hs.div;
            });
        }
        // hotspotsCreated = false;
        // delete config.hotSpots;
    }

    _escapeHTML(s) {
        if (!this._initialConfig.escapeHTML)
            return String(s).split('\n').join('<br>');
        return String(s).split(/&/g).join('&amp;')
            .split('"').join('&quot;')
            .split("'").join('&#39;')
            .split('<').join('&lt;')
            .split('>').join('&gt;')
            .split('/').join('&#x2f;')
            .split('\n').join('<br>');  // Allow line breaks
    }

    _finishMoving() {
        this._movingThrottle.cancel();
        this._movingDebounce.cancel();
        this.sendSyncAction("e3-viewer-send-coord", cloneDeep(this._current));
    }

    _finishInteracting(eventObjects) {
        if (!this._isUserInteracting) {
            return;
        }

        this._finishMoving();

        this._isUserInteracting = false;
        this._interactionData.dist = -1;

        if (Date.now() - this._latestInteractionTime > 15) {
            // Prevents jump when user rapidly moves mouse, stops, and then
            // releases the mouse button
            this._speed.pitch = this._speed.yaw = 0;
        }
        this._container.classList.add('pnlm-grab');
        this._container.classList.remove('pnlm-grabbing');
        this._latestInteractionTime = Date.now();
        this._onStateChange('end', 'coord');
    }

    _getFilters(sceneId) {
        let filter = sceneId ? this._initialConfig.scenes[sceneId].filters : this._current.filters;
        if (filter) {
            return typeof filter === 'string' ? JSON.parse(filter) : filter;
        }
        return null;
    }

    _getHotspotScale(hfov) {
        let width, height;

        if (this._containerParams.width) {
            ({width, height} = this._containerParams)
        } else {
            width = this._container.offsetWidth;
            height = this._container.offsetHeight;
        }

        let ratio = width <= 768 && (width / height) < minAspect ? Math.pow(768 / width, 1) : 1;
        return 0.08 / hfov * width * ratio;
    }

    _getOrientationSupport() {
        return new Promise((resolve) => {
            if (window.DeviceOrientationEvent) {
                window.addEventListener('deviceorientation', deviceOrientationTest);
            } else {
                resolve(false);
            }

            function deviceOrientationTest(e) {
                window.removeEventListener('deviceorientation', deviceOrientationTest);
                if (e && e.alpha !== null && e.beta !== null && e.gamma !== null) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            }
        })
    }

    _getRenderParams(screenshotFormat) {
        return {
            // roll: this._current.roll,
            returnImage: screenshotFormat
        };
    }

    //++
    _init() {
        // Display an error for IE 9 as it doesn't work but also doesn't otherwise
        // show an error (older versions don't work at all)
        // Based on: http://stackoverflow.com/a/10965203
        let div = document.createElement("div");
        div.innerHTML = "<!--[if lte IE 9]><i></i><![endif]-->";
        if (div.getElementsByTagName("i").length === 1) {
            this._anError();
            return;
        }

        this._origHfov = this._current.hfov;
        this._origPitch = this._current.pitch;
        this._mresConfig = cloneDeep(this._current.multiRes);

        this._container.classList.add('pnlm-grab');
        this._container.classList.remove('pnlm-grabbing');
        this._initRenderer();
    }

    _initHotspotEvents(hotspotData) {
        hotspotData.onMove = this._preventEvent;
        hotspotData.onTap = this._onHotspotTap.bind(this, hotspotData);
        hotspotData.onMouseDown = (e) => { e.stopPropagation(); };
        hotspotData.div.addEventListener('mousedown', hotspotData.onMouseDown, false);
        hotspotData.div.addEventListener('click', hotspotData.onTap, false);
        hotspotData.div.style.cursor = 'pointer';
        if (hotspotData.type !== 'info') {
            hotspotData.div.addEventListener('touchmove', hotspotData.onMove);
            hotspotData.div.addEventListener('touchend', hotspotData.onTap, false);
            hotspotData.div.style.touchAction = 'none';
            if (hotspotData.span) {
                hotspotData.span.style.cursor = 'pointer';
                hotspotData.span.style.touchAction = 'none';
            }

            this._initHotspotTooltip(hotspotData);
        }

    }

    _initHotspotTooltip(hs) {
        let {id} = hs;

        let src = this._getScenePreview(id);
        this._hotspotTooltip.add(id, hs.div, src, {yaw: hs.targetYaw});
    }

    _getScenePreview(id) {
        let scene = this._initialConfig.scenes[id], src;

        try {
            src = `${scene.multiRes.basePath}preview.jpg`
        } catch (e) {
            console.error(e);
            throw 'Scene not found!';
        }

        return src;
    }

    _destroyHotspotTooltip(hs) {
        let {id} = hs;
        this._hotspotTooltip.remove(id);
    }

    _destroyHotSpotEvents(hs) {
        if (!hs.div) return;
        hs.div.removeEventListener('mousedown', hs.onMouseDown, false);
        hs.div.removeEventListener('click', hs.onTap, false);
        hs.div.removeEventListener('touchend', hs.onTap, false);
        hs.div.removeEventListener('touchmove', hs.onMove);

        this._destroyHotspotTooltip(hs);
    }

    _initRenderer() {
        if (!this._renderer)
            this._renderer = new Renderer(this._renderContainer, this._disableDevicePixelRatio, this._permissions);

        // Only add event listeners once
        if (!this._listenersInited) {
            this._listenersInited = true;
            document.addEventListener('mousemove', this._onDocumentMouseMoveBound, false);
            document.addEventListener('mouseup', this._onDocumentMouseUpBound, false);
            document.addEventListener('mouseleave', this._onDocumentMouseUpBound, false);
            window.addEventListener('keydown', this._onKeyDown, false);
            window.addEventListener('keyup', this._onKeyUp, false);
            window.addEventListener('focus', this._onDocumentResizeBound, false);
            window.addEventListener('resize', this._onDocumentResizeBound, false);
            window.addEventListener('orientationchange', this._onDocumentResizeBound, false);
            this._container.addEventListener('keydown', this._onDocumentKeyPressBound, false);
            this._container.addEventListener('keyup', this._onDocumentKeyUpBound, false);
            this._container.addEventListener('blur', this._clearKeysBound, false);
            this._container.addEventListener('mozfullscreenchange', this._onFullScreenChangeBound, false);
            this._container.addEventListener('webkitfullscreenchange', this._onFullScreenChangeBound, false);
            this._container.addEventListener('msfullscreenchange', this._onFullScreenChangeBound, false);
            this._container.addEventListener('fullscreenchange', this._onFullScreenChangeBound, false);
            this._dragFix.addEventListener('mousedown', this._onDocumentMouseDownBound, false);
            this._dragFix.addEventListener('touchstart', this._onDocumentTouchStartBound, {passive: false});
            this._dragFix.addEventListener('touchmove', this._onDocumentTouchMoveBound, {passive: false});
            this._dragFix.addEventListener('touchend', this._onDocumentTouchEndBound, false);
            this._dragFix.addEventListener('pointerdown', this._onDocumentPointerDownBound, false);
            this._dragFix.addEventListener('pointermove', this._onDocumentPointerMoveBound, false);
            this._dragFix.addEventListener('pointerup', this._onDocumentPointerUpBound, false);
            this._dragFix.addEventListener('pointerleave', this._onDocumentPointerUpBound, false);
            if (this._current.mouseZoom) {
                // this._container.addEventListener('mousewheel', this._onDocumentMouseWheelBound, false);
                this._container.addEventListener('wheel', this._onDocumentMouseWheelBound, false);
                this._container.addEventListener('DOMMouseScroll', this._onDocumentMouseWheelBound, false);
            }
            if (this._current.doubleClickZoom) {
                this._dragFix.addEventListener('dblclick', this._onDocumentDoubleClickBound, false);
            }

            // Deal with MS pointer events
            if (window.navigator.pointerEnabled)
                this._container.style.touchAction = 'none';
        }

        this._renderInit();
    }

    _isActionKeyDown(keys = []) {
        if (!keys || !keys.length) {
            return this._keysDown.some(Boolean);
        }
        return keys.some(k => this._keysDown[k]);
    }

    _getSpeedAnimation() {
        return this._isAcessibility ?  10 : 1000;
    }

    _getDirection(yaw) {
        return this._isAcessibility ?  -1 * yaw : yaw;
    }

    _keyRepeat() {
        // Only do something if the panorama is loaded
        if (!this._loaded) {
            return;
        }
        let isKeyDown = false;

        let prevPitch = this._current.pitch;
        let prevYaw = this._current.yaw;
        let prevZoom = this._current.hfov;

        let newTime = Pannellum.performance.now();
        if (this._prevAnimationTime === null) {
            this._prevAnimationTime = newTime;
        }
        let timeDiff = (newTime - this._prevAnimationTime) / this._getSpeedAnimation();
        let diff = timeDiff * this._current.hfov / 1.7;
        diff = Math.min(diff, 1.0);

        // If minus key or zoom-out control is down
        if (this._keysDown[0] || this._keysDown[15]) {
            this._setHfov(this._current.hfov + (this._speed.hfov * 0.8 + 0.5) * diff);
            isKeyDown = true;
        }

        // If plus key or zoom-in control is down
        if (this._keysDown[1] || this._keysDown[14]) {
            this.zoom_in(diff);
            isKeyDown = true;
        }

        // If up arrow or "w" or up control is down
        if (this._keysDown[2] || this._keysDown[6] || this._keysDown[10]) {
            // Pan up
            this._current.pitch += (this._speed.pitch * 0.8 + 0.2) * diff;
            isKeyDown = true;
        }

        // If down arrow or "s" or down control is down
        if (this._keysDown[3] || this._keysDown[7] || this._keysDown[11]) {
            // Pan down
            this._current.pitch += (this._speed.pitch * 0.8 - 0.2) * diff;
            isKeyDown = true;
        }

        // If left arrow or "a" or left control is down
        if (this._keysDown[4] || this._keysDown[8] || this._keysDown[13]) {
            // Pan left
            this._current.yaw += (this._speed.yaw * 0.8 - 0.2) * diff;
            isKeyDown = true;
        }

        // If right arrow or "d" or right control is down
        if (this._keysDown[5] || this._keysDown[9] || this._keysDown[12]) {
            // Pan right
            this._current.yaw += (this._speed.yaw * 0.8 + 0.2) * diff;
            isKeyDown = true;
        }

        let now = Date.now();
        if (isKeyDown)
            this._latestInteractionTime = now;

        // If auto-rotate
        if (this._current.autoRotate) {
            // Pan
            if (timeDiff > 0.001) {
                let yawDiff = this._speed.yaw * diff - this._current.autoRotate * 0.2 * timeDiff;
                yawDiff = (this._current.autoRotate < 0 ? 1 : -1) * Math.min(Math.abs(this._current.autoRotate * timeDiff), Math.abs(yawDiff));
                this._current.yaw += this._getDirection(yawDiff);
            }
        }

        // Animated moves
        if (this._animatedMove.pitch) {
            this._animateMove('pitch');
            prevPitch = this._current.pitch;
        }
        if (this._animatedMove.yaw) {
            this._animateMove('yaw');
            prevYaw = this._current.yaw;
        }
        if (this._animatedMove.hfov) {
            this._animateMove('hfov');
            prevZoom = this._current.hfov;
        }

        // "Inertia"
        if (diff > 0 && !this._current.autoRotate) {
            // "Friction"
            //TODO: friction = 0 if in e3live room
            let friction;
            if (this._e3liveRoomId.length > 1) {
                friction = 0;
            } else {
                friction = 0.55;
            }

            // Yaw
            if (!this._animatedMove.yaw && !this._isActionKeyDown([4, 5, 8, 9, 12, 13])) {
                this._current.yaw += this._speed.yaw * diff * friction;
            }
            // Pitch
            if (!this._animatedMove.pitch && !this._isActionKeyDown([2, 3, 6, 7, 10, 11])) {
                this._current.pitch += this._speed.pitch * diff * friction;
            }
            // Zoom
            if (!this._animatedMove.hfov && !this._isActionKeyDown([0, 1, 14, 15])) {
                this._setHfov(this._current.hfov + this._speed.hfov * diff * friction);
            }
        }

        this._prevAnimationTime = newTime;
        if (diff > 0) {
            this._speed.yaw = this._speed.yaw * 0.8 + (this._current.yaw - prevYaw) / diff * 0.2;
            this._speed.pitch = this._speed.pitch * 0.8 + (this._current.pitch - prevPitch) / diff * 0.2;
            this._speed.hfov = this._speed.hfov * 0.8 + (this._current.hfov - prevZoom) / diff * 0.2;

            // Limit speed
            let maxSpeed = this._current.autoRotate ? Math.abs(this._current.autoRotate) : 5;
            this._speed.yaw = Math.min(maxSpeed, Math.max(this._speed.yaw, -maxSpeed));
            this._speed.pitch = Math.min(maxSpeed, Math.max(this._speed.pitch, -maxSpeed));
            this._speed.hfov = Math.min(maxSpeed, Math.max(this._speed.hfov, -maxSpeed));
        }

        // Stop movement if opposite controls are pressed
        if ((this._keysDown[0] || this._keysDown[15]) && (this._keysDown[1] || this._keysDown[14])) {
            this._speed.hfov = 0;
        }
        if ((this._keysDown[2] || this._keysDown[6] || this._keysDown[10]) && (this._keysDown[3] || this._keysDown[7] || this._keysDown[11])) {
            this._speed.pitch = 0;
        }
        if ((this._keysDown[4] || this._keysDown[8] || this._keysDown[13]) && (this._keysDown[5] || this._keysDown[9] || this._keysDown[12])) {
            this._speed.yaw = 0;
        }
    }

    zoom_in(diff, currentHfov, speedHfov){
        const currentHfovTest = currentHfov || this._current.hfov;
        const speedHfovTest = speedHfov || this._speed.hfov;

        this._setHfov(currentHfovTest + (speedHfovTest * 0.8 - 0.2) * diff)
    }

    _load(callback) {
        // Since WebGL error handling is very general, first we clear any error box
        // since it is a new scene and the error from previous maybe because of lacking
        // memory etc and not because of a lack of WebGL support etc
        // clearError();
        this._fireEvent('load-scene')
        this._init(callback);
    }

    _loadScene(sceneId, targetPitch, targetYaw, targetHfov, callback = () => {
    }) {
        this._currentSceneId = sceneId;
        this._loaded = false;
        this._animatedMove = {};

        this._onStateChange('start', 'changescene', {hfov: targetHfov, yaw: targetYaw, pitch: targetPitch});

        let workingPitch, workingYaw, workingHfov;

        // Set new pointing
        if (targetPitch === 'same') {
            workingPitch = this._current.pitch;
        } else {
            workingPitch = targetPitch;
        }

        if (targetYaw === 'same') {
            workingYaw = this._current.yaw;
        } else {
            workingYaw = targetYaw;
        }

        if (targetHfov === 'same') {
            workingHfov = this._current.hfov;
        } else {
            workingHfov = targetHfov;
        }

        // Destroy hot spots from previous scene
        this._destroyHotSpots();

        // Create the new config for the scene
        this._mergeConfig(sceneId);
        this._fireEvent('onscenechange', sceneId);

        // Stop motion
        this._speed = {
            yaw: 0,
            pitch: 0,
            hfov: 0
        };

        // Reload scene
        if (typeof workingPitch !== 'undefined') {
            this._current.pitch = workingPitch;
        }
        if (typeof workingYaw !== 'undefined') {
            this._current.yaw = workingYaw;
        }
        if (typeof workingHfov !== 'undefined') {
            this._setHfov(workingHfov);
        }
        this._fireEvent('scenechange', sceneId);
        this._load(callback);
        this._sendSyncActionThrottle();
        callback && callback()
    }

    _mergeConfig(sceneId) {
        let posePitchDegrees = this._current.posePitchDegrees;
        let poseRollDegrees = this._current.poseRollDegrees;
        this._current = Object.assign(this._current, cloneDeep(Pannellum._defaultConfig));
        this.changeTiming();
        this._current.posePitchDegrees = posePitchDegrees ? posePitchDegrees : 0;
        this._current.poseRollDegrees = poseRollDegrees ? poseRollDegrees : 0;

        // Merge current scene config
        if ((sceneId || sceneId === 0) && this._initialConfig.scenes && this._initialConfig.scenes[sceneId]) {
            Object.assign(this._current, this._initialConfig.scenes[sceneId]);
            this._current.sceneId = sceneId;
        }

        if (this._initialConfig.default) {
            merge(this._current, this._initialConfig.default);
            delete this._initialConfig.default.autoRotate;
            delete this._initialConfig.default.yaw;
            delete this._initialConfig.default.pitch;
            delete this._initialConfig.default.hfov;
        }
    }

    _mouseEventToCoords(event) {
        let mousePos = {clientX: 0, clientY: 0};
        if (event.clientX) mousePos = event;
        if (event.changedTouches) mousePos = event.changedTouches[0];

        let pos = this._mousePosition(mousePos);
        let [canvasWidth, canvasHeight] = this._renderer.getCanvasDimensions();
        let aspect = canvasWidth / canvasHeight;
        let hfovRad = this._normalizeHfov(this._current.hfov, aspect)[1];

        if (!this._disableDevicePixelRatio) {
            canvasWidth /= window.devicePixelRatio || 1;
            canvasHeight /= window.devicePixelRatio || 1;
        }

        let x = pos.x / canvasWidth * 2 - 1;
        let y = (1 - pos.y / canvasHeight * 2) / aspect;
        let focal = 1 / Math.tan(hfovRad / 2);
        let pitch = this._current.pitch * Pannellum.RADIAN;
        let s = Math.sin(pitch);
        let c = Math.cos(pitch);
        let a = focal * c - y * s;
        let root = Math.sqrt(x * x + a * a);
        // pitch = Math.atan((y * c + focal * s) / root) / Pannellum.RADIAN;
        // let yaw = Math.atan2(x / root, a / root) / Pannellum.RADIAN + this._current.yaw;

        // return [pitch, yaw];

        pitch = Math.atan((y * c + focal * s) / root);
        let yaw = Math.atan2(x / root, a / root) + this._current.yaw * Pannellum.RADIAN;

        // ([yaw, pitch] = this._applyCameraCorrection(yaw, pitch));

        // return rad ? [pitch, yaw] : [pitch / Pannellum.RADIAN, yaw / Pannellum.RADIAN];
        return [pitch / Pannellum.RADIAN, yaw / Pannellum.RADIAN];
    }

    _mousePosition(event) {
        let bounds = this._container.getBoundingClientRect();
        return {
            x: event.clientX - bounds.left,
            y: event.clientY - bounds.top
        };
    }

    _normalizeHfov(hfov, aspect) {
        if (aspect >= minAspect) {
            return [false, hfov * Pannellum.RADIAN];
        }

        if (this._lastNormalizedHfov[0] !== hfov || this._lastNormalizedHfov[1] !== aspect) {
            let vfov = 2 * Math.atan(Math.tan(hfov * Pannellum.RADIAN / 2) / minAspect);
            this._lastNormalizedHfov = [hfov, aspect, 2 * Math.atan(Math.tan(vfov / 2) * aspect)];
        }

        return [true, this._lastNormalizedHfov[2]];
    }

    _onDocumentDoubleClick(event) {
        if (this._movesBlocked) return;
        if (this._current.minHfov === this._current.hfov) {
            this._setHfov(this._origHfov, 1000);
        } else {
            this.lookAt(...this._mouseEventToCoords(event), this._current.minHfov, 1000);
        }
        this._onStateChange('start', 'coord');
    }

    _onDocumentKeyPress(event) {
        // Turn off auto-rotation if enabled
        if (!this._listenKeyboardEvents) return;
        this._blockScreenThrottle();
        this.stopAnimation();
        this._latestInteractionTime = Date.now();

        this._stopOrientation();
        this._current.roll = 0;

        // Record key pressed
        let keynumber = event.which || event.keycode;

        // Override default action for keys that are used
        if (Pannellum._usedKeyNumbers.indexOf(keynumber) < 0)
            return;
        event.preventDefault();

        // If escape key is pressed
        if (keynumber === 27) {
            // If in fullscreen mode
            if (this._fullscreenActive) {
                this.this._toggleFullscreen();
            }
        } else {
            // Change key
            this.changeKey(keynumber, true);
        }
    }

    _onDocumentKeyUp(event) {
        // Record key pressed
        if (!this._listenKeyboardEvents) return;

        let keynumber = event.which || event.keycode;


        // Override default action for keys that are used
        if (Pannellum._usedKeyNumbers.indexOf(keynumber) < 0)
            return
        event.preventDefault();

        // Change key
        this.changeKey(keynumber, false);
    }

    _onDocumentMouseDown(event) {
        event.preventDefault();
        this._fireEvent('on-document-mouse-down');
        this._container.focus();

        if (!this._loaded) {
            return;
        }

        const target = document.elementFromPoint(event.clientX, event.clientY);
        if (target && target.closest('.pnlm-hotspot-base')) {
            return;
        }

        this._startInteracting([event]);
    }

    _onDocumentMouseMove(event) {
        if(this.activeMode === MEASUREMENT) return;
        
        this._continueInteracting([event]);
    }

    _onDocumentMouseUp(event) {
        let shouldFireEvent = this._isUserInteracting;
        this._finishInteracting([event]);
        if (shouldFireEvent) {
            this._fireEvent('mouseup', event);
            console.log('drag end — pitch:', this.getPitch(), 'yaw:', this.getYaw());
        }
    }

    onMouseUp(event) {
        this._movesBlocked = true;
        this._onDocumentMouseUp(event);
    }

    _onDocumentMouseWheel(event) {
        // Only do something if the panorama is loaded and mouse wheel zoom is enabled
        if (!this._loaded || this._movesBlocked) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this._continueMovingSync();
        // Turn off auto-rotation if enabled
        this.stopAnimation();
        this._latestInteractionTime = Date.now();

        if (event.wheelDeltaY) {
            // WebKit
            this._setHfov(this._current.hfov - event.wheelDeltaY * 0.05);
            this._speed.hfov = event.wheelDelta < 0 ? 1 : -1;
        } else if (event.wheelDelta) {
            // Opera / Explorer 9
            this._setHfov(this._current.hfov - event.wheelDelta * 0.05);
            this._speed.hfov = event.wheelDelta < 0 ? 1 : -1;
        } else if (event.deltaY) {
            // Firefox
            this._setHfov(this._current.hfov + event.deltaY * 1.5);
            this._speed.hfov = event.deltaY > 0 ? 1 : -1;
        }

        this._animateInit();
        this._onStateChange('', 'mousewheel', event.wheelDelta);
        this._fireEvent('mousewheel', {});

    }

    _onDocumentPointerDown(event) {
        if (event.pointerType === 'touch') {
            event.preventDefault();
            this._interactionData.pointerIds.push(event.pointerId);
            this._interactionData.pointerCoords.push({clientX: event.clientX, clientY: event.clientY});
            event.targetTouches = this._interactionData.pointerCoords;
            this._onDocumentTouchStart(event);
        }
    }

    _onDocumentPointerMove(event) {
        if (event.pointerType === 'touch') {
            event.preventDefault();
            let pointIndex = this._interactionData.pointerIds.findIndex(p => p === event.pointerId);
            if (pointIndex > -1) {
                this._interactionData.pointerCoords[pointIndex] = {clientX: event.clientX, clientY: event.clientY};
                event.targetTouches = this._interactionData.pointerCoords;
                this._onDocumentTouchMove(event);
            }
        }
    }

    _onDocumentPointerUp(event) {
        if (event.pointerType === 'touch') {
            event.preventDefault();
            let pointIndex = this._interactionData.pointerIds.findIndex(p => p === event.pointerId);
            if (pointIndex > -1) {
                this._interactionData.pointerIds.splice(pointIndex, 1);
                this._interactionData.pointerCoords.splice(pointIndex, 1);
            }
            if (!this._interactionData.pointerIds.length) {
                this._onDocumentTouchEnd(event);
            }
        }
    }

    _onKeyUp(e) {
        if (e.repeat) {
            return
        }

        this._fireEvent('keyUp', e);

    }

    _onKeyDown(e) {
        if (e.repeat) {
            return
        }

        this._fireEvent('keyDown', e);
    }

    fireEvent(action, obj={}){
        this._fireEvent(action, obj);
    }

    _onDocumentResize() {
        setTimeout(this._onFullScreenChangeBound, 10);
        if (window.navigator.userAgent.match(/iP(hone|od|ad)/)) {
            let iphone_version = parseFloat(parseFloat(String(window.navigator.userAgent.match(/[0-9]{2}_[0-9]/)).split('_')[0] + '.' + String(window.navigator.userAgent.match(/[0-9]_[0-9]/)).split('_')[1]));
            if (iphone_version > 10.2) {
                setTimeout(this._onFullScreenChangeBound, 500);
                setTimeout(this._onFullScreenChangeBound, 1000);
                setTimeout(this._onFullScreenChangeBound, 1500);
            }
        }
    }

    _onDocumentTouchEnd(event) {
        this._finishInteracting(event.targetTouches);
        console.log('drag end — pitch:', this.getPitch(), 'yaw:', this.getYaw());
    }

    _onDocumentTouchMove(event) {
        // Override default action
        event.preventDefault();
        let eventObjects = [event.targetTouches[0]];
        if (event.targetTouches.length === 2) {
            eventObjects.push(event.targetTouches[1]);
        }
        this._continueInteracting(eventObjects);
    }

    _onDocumentTouchStart(event) {
        this._fireEvent('on-document-touch-start');
        // Only do something if the panorama is loaded
        if (!this._loaded) {
            return;
        }

        const touch = event.targetTouches[0];
        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        if (target && target.closest('.pnlm-hotspot-base')) {
            return;
        }

        let eventObjects = [touch];
        if (event.targetTouches.length === 2) {
            eventObjects.push(event.targetTouches[1]);
        }
        this._startInteracting(eventObjects);
    }

    _onFullScreenChange() {
        this._fullscreenActive = document.fullscreen || document.mozFullScreen || document.webkitIsFullScreen || document.msFullscreenElement;
        // Resize renderer (deal with browser quirks and fixes #155)
        this._renderer.resize();
        this._updateContainerProps();
        this._setHfov(this._current.hfov);
        this._animateInit();
    }

    _updateContainerProps() {
        if (!this._container) return;

        this._containerParams = {
            width: this._container.offsetWidth,
            height: this._container.offsetHeight
        }
    }

    _onHotspotTap(hs, e) {
        e.preventDefault();
        e.stopPropagation();
        if (this._sceneHotspotsHidden && hs.type === 'scene') return;
        this._fireEvent('before.change.scene', {});
        if (!hs.clicked && hs.type === 'scene') {
            hs.clicked = true;
            if (this._orientation) {
                this._stopOrientation(true);
            }
            this.sendSyncAction("e3-viewer-send-coord", hs);
            this._fireEvent('on-start-hotspottab', hs.sceneId);
            this._fireEvent('hotspottab', hs.lineID);
            this._onStateChange('start', 'coord');
            this._animateHotspotTransition(hs, () => {
                // TODO check if we use
                this._onStateChange('end', 'coord');
                this._fireEvent('hotspotclick', hs.lineID);
                hs.clicked = false;
            });

            // this._hotspotTooltip.hideTooltip();
            this._resetHotspotConfig(hs);

        } else if (hs.type === 'info') {
            this._fireEvent('open.info', hs.id, hs.htmlURL, hs.title, hs.icon, hs.div, e, hs.size, hs.order);
        }

        return false;
    }


    _onKeyChangeOff() {
        this._onStateChange('end', 'coord');
    }

    _onKeyChangeOn() {
        this._prevAnimationTime = Pannellum.performance.now();
        this._animateInit();
        this._onStateChange('start', 'coord');
    }

    _onStateChange(eventState, eventType, additional) {
        if (!this._stateListening)
            return;
        let eventName = 'statechange' + (eventState ? '-' + eventState : '');
        let eventData = {
            type: eventType,
        };
        switch (eventType) {
            case 'coord':
            case 'mousewheel':
                eventData.hfov = this._current.hfov;
                eventData.yaw = this._current.yaw;
                eventData.pitch = this._current.pitch;
                eventData.roll = this._current.roll;
                eventData.scene = this._current.sceneId;
                eventData.wheelDelta = additional ? additional : 0;
                break;
            case 'info':
                eventData.id = additional;
                break;
            case 'changescene':
                let src = additional ? additional : this._current;
                eventData.hfov = src.hfov || 10; // hardcoded value in _animateHotspotTransition()
                eventData.yaw = src.yaw;
                eventData.pitch = src.pitch;
                eventData.roll = 0;
                eventData.scene = this._current.sceneId;
                eventData.wheelDelta = 0;
        }
        this._fireEvent(eventName, eventData);
    }

    _orientationListener(e) {
        let q = Pannellum._computeQuaternion(e.alpha, e.beta, e.gamma).toEulerAngles();
        if (typeof (this._orientation) == 'number' && this._orientation < 10) {
            this._orientation += 1;
        } else if (orientation === 10) {
            this._orientationYawOffset = q[2] / Pannellum.RADIAN + this._current.yaw;
            this._orientation = true;
            requestAnimationFrame(this._animateBound);
        } else {
            this._current.pitch = q[0] / Pannellum.RADIAN;
            // this._current.roll = -q[1] / Pannellum.RADIAN;
            this._current.yaw = -q[2] / Pannellum.RADIAN + this._orientationYawOffset;
        }
    }

    _preStartInteracting() {
        // Turn off auto-rotation if enabled
        this.stopAnimation();
        this._stopOrientation();
        this._current.roll = 0;
        this._speed.hfov = 0;
    }

    _prepareInitialConfig(initialConfig) {
        this._initialConfig = cloneDeep(initialConfig);
    }

    _preventEvent(e) {
        e.preventDefault();
    }

    _removeHotSpot(hotSpotId) {
        if (!config.hotSpots)
            return false;
        let index = this._current.hotSpots.findIndex(hs => hs.id === hotSpotId);
        let hotspot = this._current.hotSpots[index];
        if (hotspot) {
            let el = hotspot.div;
            while (el.parentNode !== this._renderContainer)
                el = el.parentNode;
            this._renderContainer.removeChild(el);
            delete config.hotSpots[i].div;
            // Remove hot spot from configuration
            config.hotSpots.splice(index, 1);
            return true;
        }
        return false;
    }

    _removeScene(sceneId) {
        if (this._current.sceneId === sceneId || !this._initialConfig.scenes.hasOwnProperty(sceneId))
            return false;

        delete this._initialConfig.scenes[sceneId];
        return true;
    }

    onAutoTuneScreenshot(data, canvasWidth, canvasHeight){
        this._fireEvent('screenshot', data, canvasWidth * 4, canvasHeight * 4);
        this._onScreenshotDone();
    }

    _render() {
        if (this._loaded) {
            // Ensure the calculated pitch is within min and max allowed
            if (this._current.pitch < this._current.minPitch) {
                this._current.pitch = this._current.minPitch;
            } else if (this._current.pitch > this._current.maxPitch) {
                this._current.pitch = this._current.maxPitch;
            }
            if (this._current.yaw > this._current.maxYaw) {
                this._current.yaw -= 360;
            } else if (this._current.yaw < this._current.minYaw) {
                this._current.yaw += 360;
            }
            let [canvasWidth, canvasHeight] = this._renderer.getCanvasDimensions();
            let hfovRes = this._normalizeHfov(this._current.hfov, canvasWidth / canvasHeight);
            // this._current.roll -= 0.01
            let data = this._renderer.render(this._current.pitch * Pannellum.RADIAN, this._current.yaw * Pannellum.RADIAN, hfovRes[1], this._getRenderParams(this._needScreenshot));
            this._renderHotSpots();
            
            this._fireEvent('animate');

            if (!this._firstRender && this._converte3video.length > 1 && window.parent) {
                this._firstRender = true;
                this._timer.set(this._sendRenderEvent.bind(this, this._timer), 20);
            }
            if (data) {
                if(this.autoTune){
                    this.onAutoTuneScreenshot(data, canvasWidth, canvasHeight);
                }else{
                    this._fireEvent('screenshot', data, canvasWidth * 4, canvasHeight * 4);
                    this._onScreenshotDone();
                }
            }
        }
    }

    _renderHotSpot(hs/*TODO: pannellum-admin add in params:, yaw, pitch*/) {
        //TODO: move to pannellum-admin
        // if (draggingHS !== hs) {
        //     pitch = hs.pitch;
        //     yaw = hs.yaw;
        // }
        // else if (typeof pitch === 'undefined') {
        //     return;
        // }
        this._renderHotSpotElement(hs.yaw, hs.pitch, hs.div, hs.span);
    }

    _applyCameraCorrection(_yaw, _pitch, _roll, posePitchDegrees, poseRollDegrees) {
        // let {roll: r, posePitchDegrees: pp, poseRollDegrees: pr} = this._current;
        // let yw = _yaw ? _yaw : this._current.yaw * Pannellum.RADIAN;
        // let pt = _pitch ? _pitch : this._current.pitch * Pannellum.RADIAN;

        let r = _roll * Pannellum.RADIAN,
            yw = _yaw * Pannellum.RADIAN,
            pt = _pitch * Pannellum.RADIAN,
            pp = posePitchDegrees * Pannellum.RADIAN,
            pr = poseRollDegrees * Pannellum.RADIAN;


        // pp *= Pannellum.RADIAN;
        // pr *= Pannellum.RADIAN;
        // r *= Pannellum.RADIAN;

        let pitch = pt, // origin value
            yaw = yw, // origin value
            x = Math.cos(pr) * Math.sin(pt) * Math.sin(pp) +
                Math.cos(pt) * (Math.cos(pp) * Math.cos(yw) +
                    Math.sin(pr) * Math.sin(pp) * Math.sin(yw)),
            y = -Math.sin(pt) * Math.sin(pr) +
                Math.cos(pt) * Math.cos(pr) * Math.sin(yw),
            z = Math.cos(pr) * Math.cos(pp) * Math.sin(pt) +
                Math.cos(pt) * (-Math.cos(yw) * Math.sin(pp) +
                    Math.cos(pp) * Math.sin(pr) * Math.sin(yw));
        pt = Math.asin(Math.max(Math.min(z, 1), -1));
        yw = Math.atan2(y, x);

        // Calculate roll
        let v = [Math.cos(pitch) * (Math.sin(pr) * Math.sin(pp) * Math.cos(yaw) -
            Math.cos(pp) * Math.sin(yaw)),
                Math.cos(pitch) * Math.cos(pr) * Math.cos(yaw),
                Math.cos(pitch) * (Math.cos(pp) * Math.sin(pr) * Math.cos(yaw) +
                    Math.sin(yaw) * Math.sin(pp))],
            w = [-Math.cos(pt) * Math.sin(yw), Math.cos(pt) * Math.cos(yw)];
        let roll_adj = Math.acos(Math.max(Math.min((v[0] * w[0] + v[1] * w[1]) /
            (Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]) *
                Math.sqrt(w[0] * w[0] + w[1] * w[1])), 1), -1));
        if (v[2] < 0)
            roll_adj = 2 * Math.PI - roll_adj;

        return [yw, pt, r + roll_adj];
    }

    // _renderHotSpotElement(yaw, pitch, element, titleElement) {
    //
    //     // let [_yaw, _pitch, _roll] = this._applyCameraCorrection();
    //     let {yaw:_yaw, pitch:_pitch, roll:_roll} = this._current;
    //
    //     let hsPitchSin = Math.sin(pitch * Pannellum.RADIAN),
    //         hsPitchCos = Math.cos(pitch * Pannellum.RADIAN),
    //         configPitchSin = Math.sin(_pitch),
    //         configPitchCos = Math.cos(_pitch),
    //         yawCos = Math.cos((-yaw) * Pannellum.RADIAN + _yaw);
    //     let z = hsPitchSin * configPitchSin + hsPitchCos * yawCos * configPitchCos;
    //     if (z <= 0 && ((yaw <= 90 && yaw > -90) || (yaw > 90 || yaw <= -90))) {
    //         element.style.visibility = 'hidden';
    //     } else {
    //         let [canvasWidth, canvasHeight] = this._renderer.getCanvasDimensions();
    //         // let aspect = canvasWidth / canvasHeight;
    //         let hfovRes = this._normalizeHfov(this._current.hfov, canvasWidth / canvasHeight);
    //         let hfov = hfovRes[0] ? hfovRes[1] / Pannellum.RADIAN : this._current.hfov;
    //         let hfovRad = hfovRes[0] ? hfovRes[1] : this._current.hfov * Pannellum.RADIAN;
    //         let yawSin = Math.sin((-yaw) * Pannellum.RADIAN + _yaw),
    //             hfovTan = Math.tan(hfovRad / 2);
    //         element.style.visibility = 'visible';
    //
    //         // Subpixel rendering doesn't work in Firefox
    //         // https://bugzilla.mozilla.org/show_bug.cgi?id=739176
    //         canvasWidth /= window.devicePixelRatio || 1;
    //         canvasHeight /= window.devicePixelRatio || 1;
    //         let coord = [-canvasWidth / hfovTan * yawSin * hsPitchCos / z / 2,
    //             -canvasWidth / hfovTan * (hsPitchSin * configPitchCos -
    //                 hsPitchCos * yawCos * configPitchSin) / z / 2];
    //         // Apply roll
    //         let rollSin = Math.sin(_roll),
    //             rollCos = Math.cos(_roll);
    //         coord = [coord[0] * rollCos - coord[1] * rollSin,
    //             coord[0] * rollSin + coord[1] * rollCos];
    //         // Apply transform
    //         coord[0] += canvasWidth >> 1;
    //         coord[1] += canvasHeight >> 1;
    //         let scaleSize = this._getHotspotScale(hfov);
    //         transform.transform(
    //             element,
    //             coord[0],
    //             coord[1],
    //             9999,
    //             scaleSize,
    //             element.classList.contains('hotspot-zoomable')
    //                 ? `rotate3d(1, 0, 0, ${-_pitch / Pannellum.RADIAN + 90}deg) rotate3d(0, 0, 1, ${_yaw / Pannellum.RADIAN}deg)`
    //                 : this._current.roll
    //         );
    //         element.dataset['scale'] = scaleSize;
    //
    //         //TODO: check if works without querying DOM
    //         // let span = element.querySelector('.pnlm-hotspot-title');
    //         if (titleElement) {
    //             if (!transform.supported) {
    //                 if (!titleElement.dataset.width) {
    //                     titleElement.dataset.width = titleElement.offsetWidth || titleElement.clientWidth;
    //                     titleElement.style.marginLeft = -(titleElement.dataset.width / 2) + 'px';
    //                     titleElement.style.top = '-30px';
    //                 }
    //             }
    //             else {
    //                 transform.transform(titleElement, '-50%', -10 - 15 / scaleSize, null, 1 / scaleSize, 0);
    //             }
    //         }_renderHotSpots
    //     }
    // }

    _renderHotSpotElement(yaw, pitch, element, titleElement, type = 'hotspot') {
        let hsPitchSin = Math.sin(pitch * Pannellum.RADIAN),
            hsPitchCos = Math.cos(pitch * Pannellum.RADIAN),
            configPitchSin = Math.sin(this._current.pitch * Pannellum.RADIAN),
            configPitchCos = Math.cos(this._current.pitch * Pannellum.RADIAN),
            yawCos = Math.cos((-yaw + this._current.yaw) * Pannellum.RADIAN);
        let z = hsPitchSin * configPitchSin + hsPitchCos * yawCos * configPitchCos;
        if (z <= 0 && ((yaw <= 90 && yaw > -90) || (yaw > 90 || yaw <= -90))) {
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
        } else {
            let [canvasWidth, canvasHeight] = this._renderer.getCanvasDimensions();
            // let aspect = canvasWidth / canvasHeight;
            let hfovRes = this._normalizeHfov(this._current.hfov, canvasWidth / canvasHeight);
            let hfov = hfovRes[0] ? hfovRes[1] / Pannellum.RADIAN : this._current.hfov;
            let hfovRad = hfovRes[0] ? hfovRes[1] : this._current.hfov * Pannellum.RADIAN;
            let yawSin = Math.sin((-yaw + this._current.yaw) * Pannellum.RADIAN),
                hfovTan = Math.tan(hfovRad / 2);
            element.style.visibility = 'visible';
            element.style.opacity = '1';

            // Subpixel rendering doesn't work in Firefox
            // https://bugzilla.mozilla.org/show_bug.cgi?id=739176

            if (!this._disableDevicePixelRatio) {
                canvasWidth /= window.devicePixelRatio || 1;
                canvasHeight /= window.devicePixelRatio || 1;
            }

            let coord = [-canvasWidth / hfovTan * yawSin * hsPitchCos / z / 2,
                -canvasWidth / hfovTan * (hsPitchSin * configPitchCos -
                    hsPitchCos * yawCos * configPitchSin) / z / 2];
            // Apply roll
            let rollSin = Math.sin(this._current.roll * Pannellum.RADIAN),
                rollCos = Math.cos(this._current.roll * Pannellum.RADIAN);
            coord = [coord[0] * rollCos - coord[1] * rollSin,
                coord[0] * rollSin + coord[1] * rollCos];
            // Apply transform
            coord[0] += canvasWidth >> 1;
            coord[1] += canvasHeight >> 1;
            let scaleSize = this._getHotspotScale(hfov);
            transform.transform(
                element,
                coord[0],
                coord[1],
                9999,
                scaleSize,
                element.classList.contains('hotspot-zoomable')
                    ? `rotate3d(1, 0, 0, ${-this._current.pitch + 90}deg) rotate3d(0, 0, 1, ${this._current.yaw}deg)`
                    : this._current.roll,
                element.classList.contains('user-icon')
            );
            element.dataset['scale'] = scaleSize;


            //TODO: check if works without querying DOM
            // let span = element.querySelector('.pnlm-hotspot-title');
            if (titleElement) {
                if (!transform.supported) {
                    if (!titleElement.dataset.width) {
                        titleElement.dataset.width = titleElement.offsetWidth || titleElement.clientWidth;
                        titleElement.style.marginLeft = -(titleElement.dataset.width / 2) + 'px';
                        titleElement.style.top = '-30px';
                    }
                } else {
                    const isNeedTranslate = element.classList.contains('user-icon');
                    transform.transform(titleElement, '-50%', -10 - 15 / scaleSize, null, 1 / scaleSize, 0, isNeedTranslate);
                }
            }
        }
    }

    _renderHotSpots() {
        this._current.hotSpots.forEach(hs => {
            this._renderHotSpot(hs);
        });
    }

    _renderInit() {
        try {
            let params = {
                filters: this._getFilters(),
                nadir_url: this._initialConfig.nadir_url
            };
            if (typeof this._current.posePitchDegrees !== 'undefined') {
                params.posePitch = this._current.posePitchDegrees * Pannellum.RADIAN;
            }
            if (typeof this._current.poseRollDegrees !== 'undefined') {
                params.poseRoll = this._current.poseRollDegrees * Pannellum.RADIAN;
            }

            this._renderer.init(this._mresConfig, params, this._renderInitCallback.bind(this));
        } catch (event) {
            console.error(event);
            // Panorama not loaded

            // Display error if there is a bad texture
            if (event.type === 'webgl error' || event.type === 'no webgl') {
                this._anError();
            } else if (event.type === 'webgl size error') {
                this._anError('This panorama is too big for your device! It\'s ' +
                    event.width + 'px wide, but your device only supports images up to ' +
                    event.maxWidth + 'px wide. Try another device.' +
                    ' (If you\'re the author, try scaling down the image.)');
            } else {
                this._anError('Unknown error. Check developer console.');
                throw event;
            }
        }
    }

    _renderInitCallback(callback) {
        this._loaded = true;
        // Show hotspots
        this._createSceneElements();
        this._onStateChange('end', 'changescene');
        this._fireEvent('load');
        this._animateInit();
    }

    _sendRenderEvent(timer) {
        if (!this._renderer.isLoading()) {
            this._fireEvent('first.rendered');
            timer.reset();
        } else {
            timer.set(this._sendRenderEvent.bind(this, timer), 20);
        }
    }

    _setHfov(hfov) {
        this._current.hfov = this._constrainHfov(hfov);
    }

    _startInteracting(eventObjects) {
        this.onMouseDown(eventObjects);
    }


    onMouseDown(eventObjects) {
        this._movesBlocked = false;
        if (this._movesBlocked) return;

        this._sendBlockScreen();
        this._preStartInteracting();
        let pos = this._mousePosition(eventObjects[0]);

        this._interactionData.x = pos.x;
        this._interactionData.y = pos.y;
        this._interactionData.yaw = this._current.yaw;
        this._interactionData.pitch = this._current.pitch;

        if (eventObjects.length === 2) {
            // Down pointer is the center of the two fingers
            let pos2 = this._mousePosition(eventObjects[1]);
            let dx = pos2.x - pos.x,
                dy = pos2.y - pos.y;
            this._interactionData.x += dx * 0.5;
            this._interactionData.y += dy * 0.5;
            this._interactionData.dist = Math.sqrt(dx * dx + dy * dy);
        }

        this._isUserInteracting = true;
        this._latestInteractionTime = Date.now();

        this._container.classList.add('pnlm-grabbing');
        this._container.classList.remove('pnlm-grab');

        this._onStateChange('start', 'coord');
        this._animateInit();
    }

    _startOrientation(withoutFiring) {
        this._orientation = 1;
        window.addEventListener('deviceorientation', this._orientationListenerBound);
        if (!withoutFiring) {
            this._fireEvent('orientation', true);
        }
        requestAnimationFrame(this._animateBound);
    }

    _stopOrientation(withoutFiring) {
        window.removeEventListener('deviceorientation', this._orientationListenerBound);
        this._orientation = false;
        if (!withoutFiring) {
            this._fireEvent('orientation', false);
        }
    }

    _toggleFullscreen(elem) {
        elem = elem || this._container;
        if (this._loaded) {
            if (!this._fullscreenActive) {
                try {
                    if (elem.requestFullscreen) {
                        elem.requestFullscreen();
                    } else if (elem.mozRequestFullScreen) {
                        elem.mozRequestFullScreen();
                    } else if (elem.msRequestFullscreen) {
                        elem.msRequestFullscreen();
                    } else {
                        elem.webkitRequestFullScreen();
                    }
                } catch (event) {
                    console.error('fullscreen not working');
                    // Fullscreen doesn't work
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                } else if (document.webkitCancelFullScreen) {
                    document.webkitCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen();
                }
            }
        }
    }

    changeKey(keynumber, value, state) {
        if (state && state() == '3d') return;
        let keyChanged = false;
        switch (keynumber) {
            // If minus key is released
            case 109:
            case 189:
            case 17:
            case 173:
                if (this._keysDown[0] !== value) {
                    keyChanged = true;
                }
                this._keysDown[0] = value;
                break;

            // If plus key is released
            case 107:
            case 187:
            case 16:
            case 61:
                if (this._keysDown[1] !== value) {
                    keyChanged = true;
                }
                this._keysDown[1] = value;
                break;

            // If up arrow is released
            case 38:
                if (this._keysDown[2] !== value) {
                    keyChanged = true;
                }
                this._keysDown[2] = value;
                break;

            // If "w" is released
            case 87:
                if (this._keysDown[6] !== value) {
                    keyChanged = true;
                }
                this._keysDown[6] = value;
                break;

            // If down arrow is released
            case 40:
                if (this._keysDown[3] !== value) {
                    keyChanged = true;
                }
                this._keysDown[3] = value;
                break;

            // If "s" is released
            case 83:
                if (this._keysDown[7] !== value) {
                    keyChanged = true;
                }
                this._keysDown[7] = value;
                break;

            // If left arrow is released
            case 37:
                if (this._keysDown[4] !== value) {
                    keyChanged = true;
                }
                this._keysDown[4] = value;
                break;

            // If "a" is released
            case 65:
                if (this._keysDown[8] !== value) {
                    keyChanged = true;
                }
                this._keysDown[8] = value;
                break;

            // If right arrow is released
            case 39:
                if (this._keysDown[5] !== value) {
                    keyChanged = true;
                }
                this._keysDown[5] = value;
                break;

            // If "d" is released
            case 68:
                if (this._keysDown[9] !== value) {
                    keyChanged = true;
                }
                this._keysDown[9] = value;
                break;

            // If up control is pressed
            case controlsKeycodes.up:
                if (this._keysDown[10] !== value) {
                    keyChanged = true;
                }
                this._keysDown[10] = value;
                break;

            // If down control is pressed
            case controlsKeycodes.down:
                if (this._keysDown[11] !== value) {
                    keyChanged = true;
                }
                this._keysDown[11] = value;
                break;

            // If right control is pressed
            case controlsKeycodes.right:
                if (this._keysDown[12] !== value) {
                    keyChanged = true;
                }
                this._keysDown[12] = value;
                break;

            // If left control is pressed
            case controlsKeycodes.left:
                if (this._keysDown[13] !== value) {
                    keyChanged = true;
                }
                this._keysDown[13] = value;
                break;

            // If zoom in control is pressed
            case controlsKeycodes.zoom_in:
                if (this._keysDown[14] !== value) {
                    keyChanged = true;
                }
                this._keysDown[14] = value;
                break;

            // If zoom out control is pressed
            case controlsKeycodes.zoom_out:
                if (this._keysDown[15] !== value) {
                    keyChanged = true;
                }
                this._keysDown[15] = value;
                break;
        }

        if (keyChanged) {
            if (value) {
                this._onKeyChangeOn();
            } else {
                this._onKeyChangeOff();
            }
        }
    }

    /**
     * Destructor.
     */
    destroy() {
        if (this._renderer)
            this._renderer.destroy();
        if (this._hotspotTooltip)
            this._hotspotTooltip.destroy();
        if (this._listenersInited) {
            document.removeEventListener('mousemove', this._onDocumentMouseMoveBound, false);
            document.removeEventListener('mouseup', this._onDocumentMouseUpBound, false);
            document.removeEventListener('mouseleave', this._onDocumentMouseUpBound, false);
            window.removeEventListener('focus', this._onDocumentResizeBound, false);
            window.removeEventListener('resize', this._onDocumentResizeBound, false);
            window.removeEventListener('orientationchange', this._onDocumentResizeBound, false);
            window.removeEventListener('keydown', this._onKeyDown, false);
            window.removeEventListener('keyup', this._onKeyUp, false);
            this._container.removeEventListener('keydown', this._onDocumentKeyPressBound, false);
            this._container.removeEventListener('keyup', this._onDocumentKeyUpBound, false);
            this._container.removeEventListener('blur', this._clearKeysBound, false);
            this._container.removeEventListener('mozfullscreenchange', this._onFullScreenChangeBound, false);
            this._container.removeEventListener('webkitfullscreenchange', this._onFullScreenChangeBound, false);
            this._container.removeEventListener('msfullscreenchange', this._onFullScreenChangeBound, false);
            this._container.removeEventListener('fullscreenchange', this._onFullScreenChangeBound, false);
            this._dragFix.removeEventListener('mousedown', this._onDocumentMouseDownBound, false);
            this._dragFix.removeEventListener('touchstart', this._onDocumentTouchStartBound, {passive: false});
            this._dragFix.removeEventListener('touchmove', this._onDocumentTouchMoveBound, {passive: false});
            this._dragFix.removeEventListener('touchend', this._onDocumentTouchEndBound, false);
            this._dragFix.removeEventListener('pointerdown', this._onDocumentPointerDownBound, false);
            this._dragFix.removeEventListener('pointermove', this._onDocumentPointerMoveBound, false);
            this._dragFix.removeEventListener('pointerup', this._onDocumentPointerUpBound, false);
            this._dragFix.removeEventListener('pointerleave', this._onDocumentPointerUpBound, false);
            if (this._current.mouseZoom) {
                this._container.removeEventListener('wheel', this._onDocumentMouseWheelBound, false);
                this._container.removeEventListener('DOMMouseScroll', this._onDocumentMouseWheelBound, false);
            }
            if (this._current.doubleClickZoom) {
                this._dragFix.removeEventListener('dblclick', this._onDocumentDoubleClickBound, false);
            }
        }
        if (!detectBrowser.isIE) this._container.innerHTML = '';
        this._container.classList.remove('pnlm-container');
        this._container.classList.remove('pnlm-grab');
        this._container.classList.remove('pnlm-grabbing');
        this._listenersInited = false;
    }

    fireOnRender() {
        this._firstRender = false;
    };

    getHfov() {
        return this._current.hfov;
    }

    getOrientation() {
        return this._orientation;
    }

    getPitch() {
        return this._current.pitch;
    }

    //TODO: check executions
    getRenderer() {
        return this._renderer;
    }

    getScene() {
        return this._current.sceneId;
    }

    getYaw() {
        return this._current.yaw;
    }

    getHFov() {
        return this._current.hfov;
    }

    isLoaded() {
        return this._loaded;
    }

    isWebglSupported() {
        return this._renderer.isWebglSupported();
    }

    loadScene(sceneId, pitch, yaw, hfov, callback) {
        if (this._loaded) {
            if (this._museumMode && this._fadetransition) {
                this._zoomEffectFromCenter(() => this._loadScene(sceneId, pitch, yaw, hfov, callback));
            } else {
                this._loadScene(sceneId, pitch, yaw, hfov, callback);
            }
        }
        return this;
    }

    lookAt(pitch, yaw, hfov, animated, callback, callbackArgs) {
        animated = typeof animated === 'undefined' ? 1000 : Number(animated);
        if (pitch || pitch === 0) {
            if(this.horizontalTransitions && !this._museumMode) pitch = 0;

            this.setPitch(pitch, animated, callback, callbackArgs);
            callback = null;
        }
        if (yaw || yaw === 0) {
            this.setYaw(yaw, animated, callback, callbackArgs);
            callback = null;
        }
        if (hfov || hfov === 0)
            this.setHfov(hfov, animated, callback, callbackArgs);
        return this;
    }

    makeScreenshot(screenshotDataType = 'image/jpeg', {hidePreloader= false}, autoTune) {
        if (this._needScreenshot) {
            return;
        }

        if(!hidePreloader) {
            this._renderContainer.classList.add('making-screenshot');
        }

        this.autoTune = autoTune;
        const [w, h] = this._renderer.getCanvasDimensions();
        let width = Math.floor((this._mresConfig.tileResolution << (this._mresConfig.maxLevel - 1)) * Math.tan(this._current.hfov * Math.PI / 360) * 0.307);
        const aspect = w / h;
        let height = width / aspect;

        let scale = size4K.height > height ? size4K.height / height : 1;
        if (width < height) scale = size4K.width > width ? size4K.width / width : 1;
        width *= scale;
        height *= scale;

        if(detectBrowser.is_iOS){
            let size = limitSize({width, height}, MAXIMUM_PIXEL_IOS);
            width = size.width;
            height = size.height;
        }

        this._renderer.setSize({width, height}, w / width * (window.devicePixelRatio || 1), false);
        this._needScreenshot = screenshotDataType;
        this._animateInit();
    }

    _onScreenshotDone() {
        this._needScreenshot = null;
        this._renderContainer.classList.remove('making-screenshot');
        this._renderer.setSize();
        this._render();
        if (this._fallbackCanvas) {
            this._renderContainer.removeChild(this._fallbackCanvas);
            this._fallbackCanvas = null;
        }
    }

    canSendAction() {
        this._canSendAction = true;
    }

    showBlockScreen() {
        this.blockedBox.show();
        this._movesBlocked = true;
        this._canSendAction = false;
        this._finishInteracting();
    }

    hideBlockScreen() {
        this.blockedBox.hide();
        this._movesBlocked = false;
        this._canSendAction = true;
    }

    moveScene(data) {
        let scene = data.scene;
        let isAnotherScene = this.getScene() != scene;
        if (scene && isAnotherScene) {
            if (data.targetYaw && data.targetHfov) {
                this._animateHotspotTransition(data, () => {
                    setTimeout(() => {
                        this.loadScene(scene, data.targetPitch, data.targetYaw, data.targetHfov);
                    }, 50);
                });
            } else {
                this.loadScene(scene, data.pitch, data.yaw, data.hfov);
            }
        } else {
            data.pitch !== null && this.getPitch() !== data.pitch && this.setPitch(data.pitch, 1000);
            data.yaw !== null && this.getYaw() !== data.yaw && this.setYaw(isAnotherScene ? -data.yaw : data.yaw, 1000);
            data.hfov !== null && this.getHfov() !== data.hfov && this.setHfov(data.hfov, 1000)
        }
    }

    resize() {
        this._onDocumentResize();
    }

    setHfov(hfov, animated, callback, callbackArgs) {
        animated = typeof animated === 'undefined' ? 1000 : Number(animated);
        if (animated) {
            this._animatedMove.hfov = {
                'startTime': Date.now(),
                'startPosition': this._current.hfov,
                'endPosition': this._constrainHfov(hfov),
                'duration': animated,
                'callback': callback,
                'callbackArgs': callbackArgs
            }
        } else {
            this._setHfov(hfov);
        }
        this._animateInit();
        return this;
    }

    setPitch(pitch, animated, callback, callbackArgs) {
        animated = typeof animated === 'undefined' ? 1000 : Number(animated);
        if (animated) {
            this._animatedMove.pitch = {
                'startTime': Date.now(),
                'startPosition': this._current.pitch,
                'endPosition': pitch,
                'duration': animated,
                'callback': callback,
                'callbackArgs': callbackArgs
            }
        } else {
            this._current.pitch = pitch;
        }
        this._animateInit();
        return this;
    }

    setYaw(yaw, animated, callback, callbackArgs) {
        animated = typeof animated === 'undefined' ? 1000 : Number(animated);
        yaw = (yaw + 540) % 360 - 180;
        if (animated) {
            let diff = yaw - this._current.yaw;
            if (Math.abs(diff) > 180) {
                this._current.yaw += 360 * Math.sign(diff);
            }

            this._animatedMove.yaw = {
                'startTime': Date.now(),
                'startPosition': this._current.yaw,
                'endPosition': yaw,
                'duration': animated,
                'callback': callback,
                'callbackArgs': callbackArgs
            }
            // if(!this._animatedMove.yaw.oldAnimated) this._animatedMove.yaw.oldAnimated = 0;
            // this._animatedMove.yaw.oldAnimated +=  animated ;
            // this._animatedMove.yaw.oldStartPosition =  this._current.yaw ;
            // this._animatedMove.yaw.oldStartTime =  Date.now() ;
        } else {
            this._current.yaw = yaw;
        }
        this._animateInit();
        return this;
    }

    stopAnimation() {
        this._animatedMove = {};
        this._current.autoRotate = false;
    }

    stopAutoRotate() {
        config.autoRotate = false;
        return this;
    }

    startAutoRotate(isAcessibility) {
        this._isAcessibility = isAcessibility;
        this._current.autoRotate = true;
        this._animateInit();
    }

    toggleFullscreen(elem) {
        this._toggleFullscreen(elem);
        return this;
    }

    toggleOrientation() {
        if (this._orientation)
            this._stopOrientation();
        else
            this._startOrientation();
    }

    toggleKeyboardListeners(enabled = true) {
        this._listenKeyboardEvents = enabled
    }

    getHSAnimatedContainerClassName() {
        return this._animatedHotspots.getContainerClass();
    }

    isHSAnimated() {
        return this._animatedHotspots.isAnimated(this._initialConfig.hotspot_url);
    }
}












